import { Calendar, Loader2, Save } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";

const DAY_LABELS = {
  mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday",
  fri: "Friday", sat: "Saturday", sun: "Sunday",
};

export default function SchedulerSettingsCard({
  darkMode, isEnabled, setIsEnabled,
  scheduleType, setScheduleType,
  cronDayOfWeek, setCronDayOfWeek,
  cronHour, setCronHour,
  cronMinute, setCronMinute,
  intervalHours, setIntervalHours,
  saving, saveConfig,
}) {
  return (
    <div className={`rounded-xl overflow-hidden ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`} data-testid="scheduler-settings">
      <div className={`px-5 py-4 border-b ${darkMode ? 'border-[#21262d]' : 'border-gray-100'}`}>
        <h2 className={`font-semibold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          <Calendar className="w-5 h-5 text-[#DC143C]" />
          Scheduler Configuration
        </h2>
      </div>
      <div className="p-5 space-y-5">
        {/* Enable Toggle */}
        <div className={`flex items-center justify-between p-4 rounded-lg ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
          <div>
            <Label className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Enable Automation
            </Label>
            <p className={`text-sm mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Run intelligence pipeline on schedule
            </p>
          </div>
          <Switch
            checked={isEnabled}
            onCheckedChange={setIsEnabled}
            className="data-[state=checked]:bg-[#DC143C]"
            data-testid="scheduler-toggle"
          />
        </div>

        {/* Schedule Type Toggle */}
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
          <Label className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Schedule Type</Label>
          <p className={`text-sm mt-0.5 mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Choose how the auto-scan runs
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline" size="sm"
              onClick={() => setScheduleType("cron")}
              className={scheduleType === "cron" ? 'bg-[#DC143C]/10 border-[#DC143C]/30 text-[#DC143C]' : darkMode ? 'border-[#30363d] text-gray-400 hover:bg-[#30363d]' : ''}
              data-testid="schedule-type-cron"
            >
              Weekly (Cron)
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={() => setScheduleType("interval")}
              className={scheduleType === "interval" ? 'bg-[#DC143C]/10 border-[#DC143C]/30 text-[#DC143C]' : darkMode ? 'border-[#30363d] text-gray-400 hover:bg-[#30363d]' : ''}
              data-testid="schedule-type-interval"
            >
              Interval (Hours)
            </Button>
          </div>
        </div>

        {scheduleType === "cron" ? (
          <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
            <Label className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Weekly Schedule</Label>
            <p className={`text-sm mt-0.5 mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Auto-scan runs every week on this day & time
            </p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className={`text-xs mb-1 block ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Day</Label>
                <select
                  value={cronDayOfWeek}
                  onChange={(e) => setCronDayOfWeek(e.target.value)}
                  className={`w-full rounded-md px-3 py-2 ${darkMode ? 'bg-[#161b22] border border-[#30363d] text-white' : 'border border-gray-200'}`}
                  data-testid="cron-day-select"
                >
                  {Object.entries(DAY_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className={`text-xs mb-1 block ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Hour (24h)</Label>
                <Input
                  type="number" min={0} max={23} value={cronHour}
                  onChange={(e) => setCronHour(Math.max(0, Math.min(23, parseInt(e.target.value) || 0)))}
                  className={darkMode ? 'bg-[#161b22] border-[#30363d] text-white' : ''}
                  data-testid="cron-hour-input"
                />
              </div>
              <div>
                <Label className={`text-xs mb-1 block ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Minute</Label>
                <Input
                  type="number" min={0} max={59} value={cronMinute}
                  onChange={(e) => setCronMinute(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                  className={darkMode ? 'bg-[#161b22] border-[#30363d] text-white' : ''}
                  data-testid="cron-minute-input"
                />
              </div>
            </div>
            <p className={`text-xs mt-3 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
              Next scan: every {DAY_LABELS[cronDayOfWeek]} at {String(cronHour).padStart(2, "0")}:{String(cronMinute).padStart(2, "0")}
            </p>
          </div>
        ) : (
          <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
            <Label htmlFor="interval" className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Run Interval (hours)
            </Label>
            <p className={`text-sm mt-0.5 mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              How often to run the intelligence pipeline
            </p>
            <div className="flex items-center gap-4">
              <Input
                id="interval" type="number" min={1} max={168} value={intervalHours}
                onChange={(e) => setIntervalHours(parseInt(e.target.value) || 72)}
                className={`w-28 ${darkMode ? 'bg-[#161b22] border-[#30363d] text-white' : ''}`}
                data-testid="interval-input"
              />
              <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
                = {Math.round(intervalHours / 24 * 10) / 10} days
              </span>
            </div>
            <div className="flex gap-2 mt-4">
              {[24, 48, 72, 168].map((hours) => (
                <Button
                  key={hours}
                  variant="outline" size="sm"
                  onClick={() => setIntervalHours(hours)}
                  className={intervalHours === hours ? 'bg-[#DC143C]/10 border-[#DC143C]/30 text-[#DC143C]' : darkMode ? 'border-[#30363d] text-gray-400 hover:bg-[#30363d]' : ''}
                  data-testid={`preset-${hours}`}
                >
                  {hours === 24 ? '1 day' : hours === 48 ? '2 days' : hours === 72 ? '3 days' : '1 week'}
                </Button>
              ))}
            </div>
          </div>
        )}

        <Button
          onClick={saveConfig}
          disabled={saving}
          className="w-full bg-[#DC143C] hover:bg-[#b91031] text-white"
          data-testid="save-scheduler-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Settings
        </Button>
      </div>
    </div>
  );
}
